import { formatDistanceToNow } from 'date-fns'
import { fr } from 'date-fns/locale'
import {
  Bullhorn,
  WarningAlt,
  ErrorFilled,
  Information,
} from '@carbon/icons-react'
import type { AnnouncementResponse } from '@sphere/communication/domain'
import { AnnouncementPriority } from '@sphere/communication/domain'
import styles from './NotificationFeedItem.module.scss'

export interface NotificationFeedItemProps {
  announcement: AnnouncementResponse
  onOpen: () => void
}

function PriorityIcon({ priority }: { priority: AnnouncementPriority }) {
  switch (priority) {
    case AnnouncementPriority.URGENT:
      return <ErrorFilled size={20} className={styles.iconUrgent} />
    case AnnouncementPriority.HIGH:
      return <WarningAlt size={20} className={styles.iconHigh} />
    case AnnouncementPriority.LOW:
      return <Information size={20} className={styles.iconLow} />
    default:
      return <Bullhorn size={20} className={styles.iconNormal} />
  }
}

function priorityLabel(priority: AnnouncementPriority): string | null {
  switch (priority) {
    case AnnouncementPriority.URGENT:
      return 'Urgent'
    case AnnouncementPriority.HIGH:
      return 'Important'
    case AnnouncementPriority.LOW:
      return 'Info'
    default:
      return null
  }
}

export function NotificationFeedItem({
  announcement,
  onOpen,
}: NotificationFeedItemProps) {
  const isUnread = !announcement.isRead
  const label = priorityLabel(announcement.priority)
  const relativeTime = formatDistanceToNow(
    new Date(announcement.publishedAt ?? announcement.createdAt),
    { addSuffix: true, locale: fr }
  )

  return (
    <button
      type="button"
      className={`${styles.item} ${isUnread ? styles.itemUnread : styles.itemRead}`}
      onClick={onOpen}
    >
      <div className={styles.iconWrap} aria-hidden>
        <PriorityIcon priority={announcement.priority} />
      </div>

      <div className={styles.body}>
        <div className={styles.topRow}>
          <h3 className={styles.title}>{announcement.title}</h3>
          <div className={styles.timeGroup}>
            {isUnread && (
              <span className={styles.unreadMarker} aria-label="Non lu" />
            )}
            <time
              className={styles.time}
              dateTime={new Date(
                announcement.publishedAt ?? announcement.createdAt
              ).toISOString()}
            >
              {relativeTime}
            </time>
          </div>
        </div>

        <p className={styles.meta}>
          <span>{announcement.authorName}</span>
          {announcement.classId && (
            <>
              <span className={styles.metaDot} aria-hidden>
                ·
              </span>
              <span>Classe</span>
            </>
          )}
          {label && (
            <>
              <span className={styles.metaDot} aria-hidden>
                ·
              </span>
              <span className={styles.priorityLabel}>{label}</span>
            </>
          )}
        </p>

        <p className={styles.preview}>{announcement.content}</p>
      </div>
    </button>
  )
}
